import { useEffect, useState } from 'react'
import { useAuthStore } from '../store/authStore'
import { authService } from '../services/authService'
import { useNavigate, useParams } from 'react-router-dom'
import { ROUTES, getAccountRoute } from '@shared/constants'
import { DashboardMetrics } from '../../shared/types/dashboard'
import Nav from '../components/Nav'
import AccountSummary from '../components/home/AccountSummary'
import GrowthChart from '../components/home/GrowthChart'
import AlertsInsightsCarousel from '../components/home/AlertsInsightsCarousel'
import CoachTip from '../components/home/CoachTip'
import MissionProgress from '../components/home/MissionProgress'
import TopVideoHighlights from '../components/home/TopVideoHighlights'

/**
 * Main dashboard view after successful authentication.
 * Redirects to AccountsPage if no TikTok account is linked.
 * Provides navigation to other features and displays user status.
 * Supports URL-based account selection via /dashboard/@username pattern.
 */

// Format date as "Last update: Feb 7 - 1:30pm"
const formatLastUpdate = (dateString: string): string => {
  const date = new Date(dateString)
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = monthNames[date.getMonth()]
  const day = date.getDate()
  let hours = date.getHours()
  const minutes = date.getMinutes()
  const ampm = hours >= 12 ? 'pm' : 'am'
  hours = hours % 12 || 12
  const minutesStr = minutes.toString().padStart(2, '0')
  return `Last update: ${month} ${day} - ${hours}:${minutesStr}${ampm}`
}

export default function DashboardPage() {

    const { username: urlUsername } = useParams<{ username?: string }>()
    const { user, accounts, accountsLoaded, logout, setActiveAccount } = useAuthStore()
    const navigate = useNavigate()
    const [dashboardData, setDashboardData] = useState<DashboardMetrics | null>(null)
    const [isDataLoading, setIsDataLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [period, setPeriod] = useState(localStorage.getItem('adam_dashboard_period') || '7d')

    const activeAccount = accounts.find(a => a.isActive)
    const normalizedUrlUsername = (urlUsername || '').replace(/^@/, '').toLowerCase()
    const accountFromUrl = urlUsername
        ? accounts.find(acc => (acc.username || '').toLowerCase() === normalizedUrlUsername)
        : undefined

    // Redirect to accounts page if no TikTok accounts are linked
    useEffect(() => {
        if (accountsLoaded && accounts.length === 0) {
            navigate(ROUTES.ACCOUNTS, { replace: true })
        }
    }, [accountsLoaded, accounts.length, navigate])

    // Sync URL with active account or set account from URL
    useEffect(() => {
        if (!accountsLoaded || accounts.length === 0) return

        // If URL has username, set it as active (supports deep linking & multi-tab)
        if (urlUsername) {
            if (accountFromUrl) {
                // Only set active if it's different to avoid loops
                if (!activeAccount || accountFromUrl.id !== activeAccount.id) {
                    setActiveAccount(accountFromUrl.id)
                }
            } else {
                // Invalid username in URL, redirect to accounts page
                navigate(ROUTES.ACCOUNTS, { replace: true })
            }
        } else if (activeAccount) {
            // No username in URL but we have active account, update URL
            navigate(getAccountRoute(activeAccount.username), { replace: true })
        }
    }, [urlUsername, accountFromUrl?.id, accountsLoaded, accounts.length, activeAccount?.id, navigate, setActiveAccount])

    // Fetch real dashboard data when active account OR period changes
    useEffect(() => {
        if (activeAccount?.username) {
            setIsDataLoading(true)
            authService.getDashboardData(activeAccount.username, period)
                .then(res => {
                    if (res.success) {
                        setDashboardData(res.data)
                    }
                })
                .finally(() => setIsDataLoading(false))
        }
    }, [activeAccount?.username, period])

    // --- All hooks are above this line. Early returns below. ---

    // Show loading state while accounts are being loaded or syncing
    if (!accountsLoaded || (urlUsername && !activeAccount && accounts.length > 0)) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="text-slate-400">Loading...</div>
            </div>
        )
    }

    // If the user deep-linked to an account they don't have access to, show a friendly fallback.
    // (We also trigger a redirect to AccountsPage via the effect above.)
    if (urlUsername && accountsLoaded && accounts.length > 0 && !accountFromUrl) {
        return (
            <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
                <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-6 text-center">
                    <h2 className="text-lg font-bold mb-2">Account not available</h2>
                    <p className="text-slate-400 text-sm mb-4">
                        You don’t have access to <span className="font-mono text-slate-200">@{normalizedUrlUsername}</span> on this session.
                    </p>
                    <button
                        type="button"
                        onClick={() => navigate(ROUTES.ACCOUNTS, { replace: true })}
                        className="w-full bg-rose-600 hover:bg-rose-500 text-white font-bold py-3 rounded-lg transition"
                    >
                        Go to Accounts
                    </button>
                </div>
            </div>
        )
    }

    // Persistence for period
    const handlePeriodChange = (newPeriod: string) => {
        setPeriod(newPeriod)
        localStorage.setItem('adam_dashboard_period', newPeriod)
    }

    // Handle manual refresh with cache bypass
    const handleRefresh = async () => {
        if (!activeAccount?.username || isRefreshing) return
        
        setIsRefreshing(true)
        try {
            const res = await authService.getDashboardData(activeAccount.username, period, true)
            if (res.success) {
                setDashboardData(res.data)
            }
        } catch (error) {
            console.error('Failed to refresh data:', error)
        } finally {
            setIsRefreshing(false)
        }
    }


    /**
     * Terminates user session and redirects to login.
     * Clears both client state and server session.
     */


    const handleLogout = async () => {

        try {
            await authService.logout()
            logout()
            navigate(ROUTES.LOGIN)
        } catch (error) {
            console.error('Logout failed:', error)
        }
    
    }

    // Determine if a period button should be disabled based on available data
    const isPeriodDisabled = (p: string) => {
        if (!dashboardData?.dataStartDate) return false;
        
        const firstDate = new Date(dashboardData.dataStartDate);
        const now = new Date();
        const diffDays = Math.floor((now.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24));
        
        // Use user's logic: if data is 2.5 months (75 days), 3m (90 days) is unusable but user said 
        // "until 3m should be usable, but 6m should be disabled".
        // So we'll be generous.
        if (p === '24h') return diffDays < 1;
        if (p === '7d') return diffDays < 4; // minimum 4 days to show a trend
        if (p === '1m') return diffDays < 15;
        if (p === '3m') return diffDays < 45; // if we have 1.5 months, show 3m
        if (p === '6m') return diffDays < 120; // 4 months minimum for 6m
        return false;
    }

    return (
        <div className="min-h-screen bg-slate-950 text-white pb-24 md:pb-8 md:pt-28 transition-colors duration-300">
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 md:mb-8">
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold text-white">
                            Welcome back, {dashboardData?.displayName || dashboardData?.username || (user?.email ? user.email.split('@')[0] : 'User')}! 👋
                        </h2>
                        <p className="text-sm md:text-base text-slate-400 mt-1">
                            Here's what's happening with your account today.
                        </p>
                        {dashboardData?.latestSnapshotAt && (
                            <div className="mt-2 flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${
                                    dashboardData.syncStatus === 'fresh' ? 'bg-emerald-500' :
                                    dashboardData.syncStatus === 'recent' ? 'bg-blue-500' :
                                    dashboardData.syncStatus === 'stale' ? 'bg-amber-500' :
                                    dashboardData.syncStatus === 'very_stale' ? 'bg-rose-500' : 'bg-slate-500'
                                }`}></div>
                                <span className="text-xs text-slate-500">
                                    {formatLastUpdate(dashboardData.latestSnapshotAt)}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-3 items-center flex-wrap">
                        <button
                            onClick={handleRefresh}
                            disabled={isRefreshing || isDataLoading}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-700 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium text-white"
                            title="Force refresh data from source"
                        >
                            <svg 
                                className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {isRefreshing ? 'Refreshing...' : 'Refresh'}
                        </button>

                        <div className="flex bg-slate-900/80 backdrop-blur-sm p-1 rounded-xl border border-slate-800">
                            {['24h', '7d', '1m', '3m', '6m'].map((p) => {
                                const disabled = isPeriodDisabled(p);
                                return (
                                    <button
                                        key={p}
                                        onClick={() => !disabled && handlePeriodChange(p)}
                                        disabled={disabled}
                                        className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                                            period === p 
                                                ? 'bg-primary-500 text-white shadow-lg' 
                                                : disabled
                                                    ? 'text-slate-600 cursor-not-allowed opacity-50'
                                                    : 'text-slate-400 hover:text-slate-200'
                                        }`}
                                    >
                                        {p.toUpperCase()}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* 1. Account Summary Bar */}
                <AccountSummary data={dashboardData} isLoading={isDataLoading} />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Main Column */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* 2. Growth Chart */}
                        <GrowthChart 
                            data={dashboardData?.chartData} 
                            comparisonData={dashboardData?.chartDataPrevious}
                            videoPosts={dashboardData?.videoPosts}
                            isLoading={isDataLoading} 
                            period={period} 
                            username={activeAccount?.username}
                        />

                        {/* 3. Alerts & Insights */}
                        <div style={{ contentVisibility: 'auto', containIntrinsicSize: '0 300px' }}>
                            <AlertsInsightsCarousel />
                        </div>

                         {/* 4. Coach Tips (High Priority) */}
                         <CoachTip />
                    </div>

                    {/* Right Side Column */}
                    <div className="space-y-8">
                         {/* 5. Mission Progress */}
                         <div className="h-auto" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 200px' }}>
                            <MissionProgress />
                         </div>

                         {/* 6. Top Video Highlights */}
                         <div className="h-auto" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 300px' }}>
                            <TopVideoHighlights 
                                video={dashboardData?.topVideo} 
                                videoPosts={dashboardData?.videoPosts} 
                                isLoading={isDataLoading} 
                                period={period}
                                username={activeAccount?.username}
                            />
                         </div>
                    </div>
                </div>
            </main>

            <Nav email={user?.email} onLogout={handleLogout} />
        </div>
    )

}
